import { AdminTitle } from "@/admin/AdminTitle.tsx";
import { ReferralAmountsButton } from "@/admin/marketing/ReferralAmountsModal.tsx";
import { referralAmountsQueryAtom } from "@/admin/marketing/referralAmountsQuery.ts";
import { referralEnabledQueryAtom, toggleEnabledMutation } from "@/admin/marketing/referralEnabledQuery.ts";
import { WAdminTable } from "@/admin/WAdminTable.tsx";
import { EditableButton } from "@/components/forms/EditableButton.tsx";
import { WSwitch } from "@/components/forms/WSwitch.tsx";
import { CurrencyPromo } from "@/components/l10n/Currency.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { Separator } from "@/components/Separator.tsx";
import { WTableBody } from "@/components/table/WTable.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { ReactNode } from "react";

export function ReferralView() {
    const enabled = useSuspenseQueryAtom(referralEnabledQueryAtom);
    const amounts = useSuspenseQueryAtom(referralAmountsQueryAtom);
    const isEnabled = enabled?.enabled;

    return (
        <>
            <AdminTitle title="Referral Program" />

            <div className="p-2">
                <p className="pb-3 color-muted">
                    Referral program motivates existing users (promoters) to invite new users (receivers) to the
                    platform.
                </p>

                <EnableDisableModal
                    isEnabled={isEnabled}
                    button={
                        <WSwitch isSelected={isEnabled} onChange={undefined}>
                            Enable / Disable
                        </WSwitch>
                    }
                />
            </div>

            <Separator />

            <WAdminTable>
                <thead>
                    <tr>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <WTableBody>
                    <tr>
                        <td>Promoter Credit</td>
                        <td>Reward given to existing user for each new user brought in</td>
                        <td>
                            <ReferralAmountsButton
                                button={
                                    <EditableButton action={undefined}>
                                        <CurrencyPromo value={amounts?.promoter_credit} />
                                    </EditableButton>
                                }
                                amounts={amounts}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Receiver Credit</td>
                        <td>
                            Additional reward given to new users for using a referral code. (Total credit given = First
                            free credit amount + Receiver Credit)
                        </td>
                        <td>
                            <ReferralAmountsButton
                                button={
                                    <EditableButton action={undefined}>
                                        <CurrencyPromo value={amounts?.receiver_credit} />
                                    </EditableButton>
                                }
                                amounts={amounts}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Receiver top up requirement</td>
                        <td>
                            Total amount of top ups required to be made by the referral code receiver before they and
                            the promoter get the reward credit.
                        </td>
                        <td>
                            <ReferralAmountsButton
                                button={
                                    <EditableButton action={undefined}>
                                        <CurrencyPromo value={amounts?.receiver_precondition_topup_sum} />
                                    </EditableButton>
                                }
                                amounts={amounts}
                            />
                        </td>
                    </tr>
                </WTableBody>
            </WAdminTable>
        </>
    );
}

function EnableDisableModal({ button, isEnabled }: { button: ReactNode; isEnabled: boolean }) {
    const toggleMutation = useStandardMutation(toggleEnabledMutation);

    async function onToggle() {
        await toggleMutation.mutateAsync({ enabled: !isEnabled });
    }

    return (
        <WModal button={button}>
            <WModalContent
                title={isEnabled ? "Disable Referral Program" : "Enable Referral Program"}
                label={isEnabled ? "Disable" : "Enable"}
                modalAction={async () => await onToggle()}
            >
                <p>
                    If Referral Program is enabled then users can see their share codes and links on Dashboard and
                    Billing Account view. Disabling the program will hide the codes for existing users and will not
                    allow previously shared codes to be used when signing up.
                </p>
            </WModalContent>
        </WModal>
    );
}
